import { HoldingConfig } from '@ddt-web/config';
import { DataServiceOperatorBuilder, DataServiceOperatorTypeEnum, LodashExtractor, OperatorExtractor, StoreNestedFilterExtractor } from '@ddt-web/data-service';
import * as flattenDeep from 'lodash/flattenDeep';

import { FactMappingInterface } from '../../../models/fact-mapping.interface';
import { CategoryConstants } from '../../constants';
import { FactExtractor } from '../fact-extractor';


export const EQUITY_FACTS: { [factName: string]: FactMappingInterface } = {
	equityAllocation: {
		extractor: new FactExtractor([
			new StoreNestedFilterExtractor(
				['details'],
				'details.portfolioBreakdowns',
				[
					{ propertyToCompare: 'name', comparedValue: 'assetAllocation', desiredValueExpression: 'sectors' }
				]
			)
		], assetAllocation => {
			return assetAllocation.reduce((sum, data) => {
				return sum + (['usStock', 'nonUsStock'].includes(data.name) ? data.contribution * 100 : 0);
			}, 0);
		})
	},

	nonUsStock: {
		extractor: new FactExtractor(
			[
				new StoreNestedFilterExtractor(
					['details'],
					'details.sleeves',
					[
						{ propertyToCompare: 'sleeveId', comparedValue: 'equity', desiredValueExpression: 'sleeveAnalysis.portfolioBreakdowns' },
						{ propertyToCompare: 'name', comparedValue: 'region', desiredValueExpression: 'sectors' }
					],
					0
				)
			],
			sectors => {
				let nonUsStock = 0;
				if (sectors) {
					const internationalGroup = [
						'africaAndMiddleEast',
						'europeEmerging',
						'asiaEmerging',
						'latinAmerica',
						'unitedKingdom',
						'europeDeveloped',
						'japan',
						'australasia',
						'asiaDeveloped',
						'canada'
					];
					nonUsStock = sectors.filter(sector =>
						internationalGroup.includes(sector.name)).reduce((acc, sector) => acc + (sector.contribution * 100), 0);
				}
				return nonUsStock;
			}
		)
	},

	mutualFundsEtfHoldings: {
		extractor: new FactExtractor([
			new StoreNestedFilterExtractor(
				['details'],
				'details.portfolioBreakdowns',
				[
					{ propertyToCompare: 'name', comparedValue: 'legalType', desiredValueExpression: 'sectors' }
				]
			)
		], legalType => {
			const mutualFundEtfsValues = flattenDeep(HoldingConfig.MUTUALFUND_ETF_PAS_PORTFOLIO_ANALYSIS.map(mfEtf => mfEtf.values)); // this is not correct, legalType via portfolio analysis has different values
			return legalType.reduce((sum, data) => {
				const returnValue = sum + (mutualFundEtfsValues.includes(data.name) ? data.contribution * 100 : 0);
				return returnValue;
			}, 0);
		})
	},

	mutualFundsEtfHoldingsCount: {
		extractor: new FactExtractor([new LodashExtractor('positionDetails.children', <any>[])], holdings => {
			const mutualFundEtfsValues = flattenDeep(HoldingConfig.MUTUALFUND_ETF.map(mfEtf => mfEtf.values));
			const mfEtfHoldings = holdings.filter(holding => mutualFundEtfsValues.includes(holding.legalType));
			return mfEtfHoldings.length;
		})
	},

	etfAllocation: {
		extractor: new FactExtractor([
			new OperatorExtractor(
				new StoreNestedFilterExtractor(
					['details'],
					'details.portfolioBreakdowns',
					[
						{ propertyToCompare: 'name', comparedValue: 'legalType', desiredValueExpression: 'sectors' },
						{ propertyToCompare: 'name', comparedValue: 'FE', desiredValueExpression: 'contribution' }
					]
				),
				100,
				DataServiceOperatorBuilder.build(DataServiceOperatorTypeEnum.MULTIPLY),
				0
			)
		], contribution => {
			return contribution;
		})
	},

	openEndAllocation: {
		extractor: new FactExtractor([
			new OperatorExtractor(
				new StoreNestedFilterExtractor(
					['details'],
					'details.portfolioBreakdowns',
					[
						{ propertyToCompare: 'name', comparedValue: 'legalType', desiredValueExpression: 'sectors' },
						{ propertyToCompare: 'name', comparedValue: 'FO', desiredValueExpression: 'contribution' }
					]
				),
				100,
				DataServiceOperatorBuilder.build(DataServiceOperatorTypeEnum.MULTIPLY),
				0
			)
		], contribution => {
			return contribution;
		})
	},

	equityCount: {
		extractor: new FactExtractor([
			new StoreNestedFilterExtractor(
				['details'],
				'details.portfolioBreakdowns',
				[
					{ propertyToCompare: 'name', comparedValue: 'assetClass', desiredValueExpression: 'sectors' }
				]
			)
		], (sectors: any[]) => {
			return sectors.find(sector => sector.name === 'Equity')?.contributors?.length ?? 0;
		})
	},

	allocationFundsAllocation: {
		extractor: new FactExtractor([
			new StoreNestedFilterExtractor(
				['details'],
				'details.portfolioBreakdowns',
				[
					{ propertyToCompare: 'name', comparedValue: 'assetClass', desiredValueExpression: 'sectors' }
				]
			)
		], (assetClass) => {
			const allocationFundAllocation = assetClass.reduce((sum, data) => {
				return sum + ('Allocation' === data.name ? data.contribution : 0);
			}, 0);
			return allocationFundAllocation * 100;
		})
	},

	equityAssetClass: {
		extractor: new FactExtractor([
			new StoreNestedFilterExtractor(
				['details'],
				'details.portfolioBreakdowns',
				[
					{ propertyToCompare: 'name', comparedValue: 'assetClass', desiredValueExpression: 'sectors' }
				]
			)
		], funds => {
			return funds
				.filter(fund => fund.name === 'Equity')
				.reduce((sum, fund) => {
					return sum + (fund.contribution * 100);
				}, 0);
		})
	},

	numberOfHoldingsAsStock: {
		extractor: new FactExtractor([new LodashExtractor('positionDetails.children')], (holdings) => {
			const stockCount = holdings.reduce((sum, holding) => {
				if (holding.assetType === 'ASSET_TYPE_STOCK') { return sum + 1; }
				else { return sum; }
			}, 0);
			return stockCount;
		})
	},

	emergingMarketsAllocation: {
		extractor: new FactExtractor([
			new StoreNestedFilterExtractor(
				['details'],
				'details.sleeves',
				[
					{ propertyToCompare: 'sleeveId', comparedValue: 'equity', desiredValueExpression: 'sleeveAnalysis.portfolioBreakdowns' },
					{ propertyToCompare: 'name', comparedValue: 'region', desiredValueExpression: 'sectors' }
				]
			)
		], region => {
			const markets = CategoryConstants.NonUsEmergingMarketCategories;
			const allocation = region ? region.filter(sector => markets.includes(sector.name)).reduce((acc, sector) => acc + sector.contribution, 0) : 0;
			return allocation * 100;
		})
	},

	assetsByMorningStarCategories: {
		extractor: new FactExtractor([new LodashExtractor('positionDetails.children')], ((holding) => {
			return holding.reduce((sum, data) => {
				return sum ?? 0 + (['$FOCA$CH$$', '$FOCA$EI$$', '$FOCA$PJ$$', '$FOCA$EM$$', '$FOCA$LS$$'].includes(data.funds.morningstarCategoryId.stringValue) ?
					data.weight : 0);
			}, 0);
		})
		)
	},

	cohortEmergingMarketsAllocation: {
		extractor: new FactExtractor([
			new StoreNestedFilterExtractor(
				['cohortBenchmarkDetail'],
				'cohortBenchmarkDetail.sleeves',
				[
					{ propertyToCompare: 'sleeveId', comparedValue: 'equity', desiredValueExpression: 'sleeveAnalysis.portfolioBreakdowns' },
					{ propertyToCompare: 'name', comparedValue: 'region', desiredValueExpression: 'sectors' }
				]
			)
		], region => {
			const markets = CategoryConstants.NonUsEmergingMarketCategories;
			const allocation = region ? region.filter(sector => markets.includes(sector.name)).reduce((acc, sector) => acc + sector.contribution, 0) : 0;
			return allocation * 100;

		})
	},

	emergingMarketVsCohortEmergingMarket: {
		extractor: new FactExtractor(
			[
				new StoreNestedFilterExtractor(
					['details'],
					'details.sleeves',
					[
						{ propertyToCompare: 'sleeveId', comparedValue: 'equity', desiredValueExpression: 'sleeveAnalysis.portfolioBreakdowns' },
						{ propertyToCompare: 'name', comparedValue: 'region', desiredValueExpression: 'sectors' }
					],
					0
				),
				new StoreNestedFilterExtractor(
					['cohortBenchmarkDetail'],
					'cohortBenchmarkDetail.sleeves',
					[
						{ propertyToCompare: 'sleeveId', comparedValue: 'equity', desiredValueExpression: 'sleeveAnalysis.portfolioBreakdowns' },
						{ propertyToCompare: 'name', comparedValue: 'region', desiredValueExpression: 'sectors' }
					],
					0
				)
			],
			(sectors, cohortSectors) => {
				const markets = CategoryConstants.NonUsEmergingMarketCategories;
				const regionSum = sectors ? sectors.filter(sector => markets.includes(sector.name)).reduce((acc, sector) => acc + sector.contribution, 0) : 0;
				const cohortRegionSum = cohortSectors ?
					cohortSectors.filter(sector => markets.includes(sector.name)).reduce((acc, sector) => acc + sector.contribution, 0) :
					0;
				return (cohortRegionSum - regionSum) * 100;
			})
	},

	portfolioVsBenchmarkNonUsEquity: {
		extractor: new FactExtractor(
			[
				new StoreNestedFilterExtractor(
					['details'],
					'details.sleeves',
					[
						{ propertyToCompare: 'sleeveId', comparedValue: 'equity', desiredValueExpression: 'sleeveAnalysis.portfolioBreakdowns' },
						{ propertyToCompare: 'name', comparedValue: 'region', desiredValueExpression: 'sectors' }
					],
					0
				),
				new StoreNestedFilterExtractor(
					['cohortBenchmarkDetail'],
					'cohortBenchmarkDetail.sleeves',
					[
						{ propertyToCompare: 'sleeveId', comparedValue: 'equity', desiredValueExpression: 'sleeveAnalysis.portfolioBreakdowns' },
						{ propertyToCompare: 'name', comparedValue: 'region', desiredValueExpression: 'sectors' }
					],
					0
				)
			],
			(sectors, cohortSectors) => {
				const internationalGroup = [
					'africaAndMiddleEast',
					'europeEmerging',
					'asiaEmerging',
					'latinAmerica',
					'unitedKingdom',
					'europeDeveloped',
					'japan',
					'australasia',
					'asiaDeveloped',
					'canada'
				];
				const portfolioEquity = sectors ?
					sectors.filter(sector => internationalGroup.includes(sector.name)).reduce((acc, sector) => acc + sector.contribution, 0) :
					0;
				const cohortEquity = cohortSectors ?
					cohortSectors.filter(sector => internationalGroup.includes(sector.name)).reduce((acc, sector) => acc + sector.contribution, 0) :
					0;

				return (cohortEquity - portfolioEquity) * 100;
			}
		)
	},

	/**
	 * Highest Single stock weight
	 */
	highestContributionStockWeight: {
		extractor: new FactExtractor([new LodashExtractor('positionDetails.children')], holdings => {
			const stocks = holdings?.filter(holding => holding?.assetType === 'ASSET_TYPE_STOCK') ?? [];
			return stocks.length > 0 ? Math.max(...stocks.map(stock => stock.weight)) : 0;
		})
	},

	/**
	 * Sum of all stocks weight
	 */
	allStocksAllocation: {
		extractor: new FactExtractor([new LodashExtractor('positionDetails.children')], holdings => {
			const stocks = holdings?.filter(holding => holding?.assetType === 'ASSET_TYPE_STOCK') ?? [];
			return stocks.length > 0 ? stocks.reduce((acc, stock) => {
				return acc += stock.weight;
			}, 0) : 0;
		})
	},

	/**
	 * Name of highest Single stock's weight
	 */
	highestContributionStockName: {
		extractor: new FactExtractor([new LodashExtractor('positionDetails.children')], holdings => {
			const stocks = holdings?.filter(holding => holding?.assetType === 'ASSET_TYPE_STOCK') ?? [];
			return stocks.reduce((prev, curr) => {
				return prev.weight > curr.weight ? prev : curr;
			})?.description;
		})
	}
};
